import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useToken } from "@packsize/authentication";

import { baseRoutes } from "@/api/base-routes";
import { useSignalRHub } from "@/hooks/use-signalr-hub";
import type { ErrorsAndWarningsProps, MachineFaultDisplay, MachineFaultType } from "@/schemas";

const websocketUrl = `${baseRoutes.x7}/hubs/x7machines`;
const errorsChangedEventName = "OnErrorsUpdated";
const warningsChangedEventName = "OnWarningsUpdated";
const subscribeWarningsMethod = "SubscribeWarnings";
const subscribeErrorsMethod = "SubscribeErrors";
const unsubscribeErrorsMethod = "UnsubscribeErrors";
const unsubscribeWarningsMethod = "UnsubscribeWarnings";

type AlarmData = {
	isActive: boolean;
	alarmType: number;
};

function getAlarmCategory(alarmCode: number, severity: MachineFaultType): string {
	if (severity === "Error") {
		switch (alarmCode) {
			case 6:
				return "ReloadZFoldTrack1";
			case 102:
				return "ReloadZFoldTrack2";
			case 4921:
				return "GluerNotReady";
			case 4922:
			case 4923:
			case 4924:
			case 4925:
				return "GlueUnitError";
			case 4926:
				return "GlueUnitOff";
			case 4993:
			case 4994:
			case 4995:
			case 4996:
			case 5001:
			case 5002:
			case 5003:
			case 5004:
			case 5005:
			case 5006:
			case 5007:
			case 5337:
			case 5338:
			case 5339:
				return "EStopTriggered";
			case 5079:
				return "LabelUnitOff";
			case 5080:
			case 5083:
				return "LabelUnitError";
			case 5081:
				return "LabelUnitNoRibbon";
			case 5082:
			case 5102:
				return "LabelUnitNoLabels";
			case 5084:
				return "NoPadLabel";
			case 5101:
				return "DownstreamConveyorStoppedError";
			case 5209:
				return "PackNetConnectionError";
			case 5353:
			case 5354:
			case 5355:
			case 5356:
			case 5357:
				return "DoorUnsecuredError";
			case 8675309:
				return "CorrugateMismatch";
			default:
				return "DefaultError";
		}
	}

	switch (alarmCode) {
		case 4975:
			return "GlueLevelLow";
		case 5009:
		case 5010:
		case 5011:
		case 5017:
		case 5018:
		case 5019:
		case 5020:
		case 5021:
		case 5022:
		case 5023:
		case 5024:
			return "DoorUnsecuredWarning";
		case 5122:
			return "LabelUnitLowRibbon";
		case 5123:
			return "LabelUnitLowLabels";
		case 5133:
			return "DownstreamConveyorStoppedWarning";
		case 6331:
			return "WaitingOnPackNet";
		default:
			return "DefaultWarning";
	}
}

function getResolution(category: string, intl: IntlShape): string | undefined {
	const translationKey = `X7 Action ${category}`;
	const resolution = intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey });

	return resolution === translationKey ? undefined : resolution;
}

function createDisplayItems(
	machineId: string,
	data: AlarmData[] | null | undefined,
	severity: MachineFaultType,
	intl: IntlShape,
): MachineFaultDisplay[] {
	if (data === undefined || data === null) {
		return [];
	}

	return data
		.filter((alarm) => alarm.isActive === true)
		.map((alarm) => {
			const category = getAlarmCategory(alarm.alarmType, severity);
			const translationKey = `X7 ${severity} ${category}`;
			const imageName = category.toLowerCase().startsWith("default") ? "default" : category.toLowerCase();

			return {
				machineId,
				title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
				resolution: getResolution(category, intl),
				image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x7/${severity.toLowerCase()}/${imageName}.png`,
				fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/x7/${severity.toLowerCase()}/default.png`,
				type: severity,
			};
		});
}

interface X7MachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
	readonly onWarningsChanged: NonNullable<ErrorsAndWarningsProps["onWarningsChanged"]>;
}

const X7MachineErrorsAndWarnings = ({
	machineId,
	onErrorsChanged,
	onWarningsChanged,
}: X7MachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { bearerToken } = useToken();
	const { hubConnection, hubConnectionEstablished, invoke, subscribe, unsubscribe } = useSignalRHub({
		hubUrl: websocketUrl,
		token: bearerToken,
	});

	useEffect(() => {
		if (hubConnection === undefined || hubConnectionEstablished === false) {
			return;
		}

		const handleErrorsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, errors] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onErrorsChanged(machineId, createDisplayItems(machineId, errors, "Error", intl));
		};

		subscribe(errorsChangedEventName, handleErrorsChanged);
		void invoke(subscribeErrorsMethod, machineId);

		return () => {
			unsubscribe(errorsChangedEventName, handleErrorsChanged);
			void invoke(unsubscribeErrorsMethod, machineId);
		};
	}, [hubConnection, hubConnectionEstablished, intl, invoke, machineId, onErrorsChanged, subscribe, unsubscribe]);

	useEffect(() => {
		if (hubConnection === undefined || hubConnectionEstablished === false) {
			return;
		}

		const handleWarningsChanged = (...args: unknown[]) => {
			const [_tenantId, eventMachineId, warnings] = args as [string, string, AlarmData[] | null | undefined];

			if (eventMachineId !== machineId) {
				return;
			}

			onWarningsChanged(machineId, createDisplayItems(machineId, warnings, "Warning", intl));
		};

		subscribe(warningsChangedEventName, handleWarningsChanged);
		void invoke(subscribeWarningsMethod, machineId);

		return () => {
			unsubscribe(warningsChangedEventName, handleWarningsChanged);
			void invoke(unsubscribeWarningsMethod, machineId);
		};
	}, [hubConnection, hubConnectionEstablished, intl, invoke, machineId, onWarningsChanged, subscribe, unsubscribe]);

	return null;
};

export const X7ErrorsAndWarnings: FC<Required<ErrorsAndWarningsProps>> = ({
	machines,
	onErrorsChanged,
	onWarningsChanged,
}) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "X7")
				.map(({ machineId }) => (
					<X7MachineErrorsAndWarnings
						key={machineId}
						machineId={machineId}
						onErrorsChanged={onErrorsChanged}
						onWarningsChanged={onWarningsChanged}
					/>
				))}
		</>
	);
};
